import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useCompanyScope } from '../context/CompanyScopeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Users, Search, Edit, Trash2, Mail, Briefcase, DollarSign, Fingerprint, AlertCircle, CheckCircle2, Eye, Building, UserX, UserCheck, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function Employees() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [biometricModalOpen, setBiometricModalOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    matricule: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    category: '',
    position: '',
    function: '',
    contract_type: '',
    hire_date: '',
    marital_status: '',
    gender: '',
    children_count: '',
    social_security_number: '',
    transport_allowance: '',
    housing_allowance: '',
    department_id: '',
    base_salary: '',
    biometric_id: '',
    company_id: '',
    is_placement: false
  });

  const canManage = ['super_admin', 'company_admin', 'hr', 'group_admin', 'group_hr'].includes(user?.role);
  const canTransfer = ['super_admin', 'group_admin', 'group_hr', 'hr'].includes(user?.role);
  const isCrossCompany = ['super_admin', 'group_admin', 'group_hr'].includes(user?.role);
  const userAllowedCompanies = user?.allowed_company_ids;
  const { companies: allCompanies, selectedCompanyId, isCrossCompany: isCross } = useCompanyScope();
  // Cross-company creators pick from all companies (or their allowed subset)
  const creatableCompanies = React.useMemo(() => {
    if (!isCrossCompany) return [];
    if (userAllowedCompanies && userAllowedCompanies.length > 0) {
      return (allCompanies || []).filter(c => userAllowedCompanies.includes(c.id));
    }
    return allCompanies || [];
  }, [isCrossCompany, userAllowedCompanies, allCompanies]);

  const [employeeOptions, setEmployeeOptions] = useState({ categories: [], positions: [] });
  // ── Transfer modal state ──
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferEmployee, setTransferEmployee] = useState(null);
  const [transferTargetCompanyId, setTransferTargetCompanyId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferCompanies, setTransferCompanies] = useState([]);

  // ── Barème primes par poste ──
  const TRANSPORT_BY_POSITION = {
    'Agent':           50,
    'Chef de service': 100,
    'Manager':         100,
    'Directeur':       100,
  };
  const HOUSING_RATE = 0.30; // 30% du salaire de base

  // Calcule et injecte les primes automatiquement
  const applyAllowanceDefaults = (position, baseSalary, currentForm) => {
    const updates = { ...currentForm };
    // Prime de transport selon le poste
    if (position && TRANSPORT_BY_POSITION[position] !== undefined) {
      updates.transport_allowance = String(TRANSPORT_BY_POSITION[position]);
    }
    // Prime de logement = 30% du salaire de base
    const salary = parseFloat(baseSalary);
    if (!isNaN(salary) && salary > 0) {
      updates.housing_allowance = String(Math.round(salary * HOUSING_RATE * 100) / 100);
    }
    return updates;
  };

  // Filtrage par entreprise sélectionnée dans le sélecteur global
  const filteredEmployees = React.useMemo(() => {
    if (!isCross || !selectedCompanyId) return employees;
    return employees.filter(e => e.company_id === selectedCompanyId);
  }, [employees, selectedCompanyId, isCross]);

  useEffect(() => {
    fetchEmployees();
    fetchDevices();
    fetchDepartments();
    fetchEmployeeOptions();
  }, []);

  const fetchEmployees = async (search = '') => {
    try {
      const response = await api.get('/employees', { params: { search } });
      setEmployees(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des employés');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Erreur chargement directions:', error);
    }
  };

  const fetchDevices = async () => {
    try {
      const response = await api.get('/zkteco/devices/status');
      setDevices(response.data || []);
    } catch (error) {
      console.error('Erreur chargement pointeurs:', error);
      setDevices([]);
    }
  };

  const fetchEmployeeOptions = async () => {
    try {
      const response = await api.get('/employee-options');
      setEmployeeOptions(response.data);
    } catch (error) {
      console.error('Erreur chargement options:', error);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchEmployees(searchTerm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        base_salary: parseFloat(formData.base_salary),
        children_count: formData.children_count ? parseInt(formData.children_count) : null,
        gender: formData.gender || null,
        transport_allowance: formData.transport_allowance ? parseFloat(formData.transport_allowance) : null,
        housing_allowance: formData.housing_allowance ? parseFloat(formData.housing_allowance) : null
      };
      // For cross-company creators, include the selected company_id (else strip empty)
      if (!payload.company_id) delete payload.company_id;
      
      if (editMode) {
        await api.put(`/employees/${selectedEmployee.id}`, payload);
        toast.success('Employé modifié avec succès');
        setOpen(false);
        resetForm();
      } else {
        const response = await api.post('/employees', payload);
        toast.success('Employé créé avec succès');
        setNewEmployee(response.data);
        setOpen(false);
        
        // Show biometric enrollment modal if no biometric_id was provided
        if (!formData.biometric_id) {
          setBiometricModalOpen(true);
        } else {
          resetForm();
        }
      }
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    }
  };

  const handleBiometricEnrollment = async (biometricId) => {
    if (!newEmployee || !biometricId) return;
    
    try {
      await api.put(`/employees/${newEmployee.id}`, { biometric_id: biometricId });
      toast.success('ID biométrique enregistré avec succès');
      setBiometricModalOpen(false);
      setNewEmployee(null);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement de l\'ID biométrique');
    }
  };

  const handleEdit = (employee) => {
    setEditMode(true);
    setSelectedEmployee(employee);
    setFormData({
      matricule: employee.matricule,
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      phone: employee.phone || '',
      category: employee.category || '',
      position: employee.position,
      function: employee.function || '',
      contract_type: employee.contract_type || '',
      hire_date: employee.hire_date || '',
      marital_status: employee.marital_status || '',
      gender: employee.gender || '',
      children_count: employee.children_count != null ? employee.children_count.toString() : '',
      social_security_number: employee.social_security_number || '',
      transport_allowance: employee.transport_allowance != null ? employee.transport_allowance.toString() : '',
      housing_allowance: employee.housing_allowance != null ? employee.housing_allowance.toString() : '',
      department_id: employee.department_id || '',
      base_salary: employee.base_salary.toString(),
      biometric_id: employee.biometric_id || '',
      is_placement: employee.is_placement || false
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const emp = employees.find(e => e.id === id);
    const label = emp ? `${emp.first_name} ${emp.last_name}` : 'cet employé';
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${label} ?`)) return;

    // Second prompt: also delete the linked user account?
    const deleteUser = window.confirm(
      `Voulez-vous aussi supprimer le compte utilisateur associé à ${label} ?\n\n` +
      `• OK / Oui : supprime l'employé ET son compte utilisateur (perte totale d'accès à l'app)\n` +
      `• Annuler / Non : supprime uniquement l'employé, le compte utilisateur reste actif`
    );

    try {
      const res = await api.delete(`/employees/${id}`, {
        params: deleteUser ? { delete_user: true } : {}
      });
      if (res.data?.user_deleted) {
        toast.success(`${label} supprimé (employé + compte utilisateur)`);
      } else {
        toast.success(`${label} supprimé (compte utilisateur conservé)`);
      }
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openTransferModal = async (employee) => {
    setTransferEmployee(employee);
    setTransferTargetCompanyId('');
    setTransferReason('');
    // Charger la liste des entreprises disponibles
    try {
      const res = await api.get('/companies');
      // Exclure l'entreprise actuelle de l'employé
      setTransferCompanies((res.data || []).filter(c => c.id !== employee.company_id));
    } catch {
      setTransferCompanies([]);
    }
    setTransferModalOpen(true);
  };

  const handleTransfer = async () => {
    if (!transferTargetCompanyId || !transferEmployee) return;
    setTransferLoading(true);
    try {
      const res = await api.post(`/employees/${transferEmployee.id}/transfer`, {
        target_company_id: transferTargetCompanyId,
        reason: transferReason || null
      });
      toast.success(res.data?.message || 'Employé transféré avec succès');
      setTransferModalOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du transfert');
    } finally {
      setTransferLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      matricule: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      category: '',
      position: '',
      function: '',
      contract_type: '',
      hire_date: '',
      marital_status: '',
      gender: '',
      children_count: '',
      social_security_number: '',
      transport_allowance: '',
      housing_allowance: '',
      department_id: '',
      base_salary: '',
      biometric_id: '',
      company_id: '',
      is_placement: false
    });
    setEditMode(false);
    setSelectedEmployee(null);
    setNewEmployee(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-amber-100 text-amber-800',
      terminated: 'bg-red-100 text-red-800'
    };
    const labels = {
      active: 'Actif',
      suspended: 'Suspendu',
      terminated: 'Renvoyé'
    };
    return (
      <Badge className={styles[status] || 'bg-slate-100'}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Employés</h1>
          <p className="text-slate-600 mt-1">Gestion des employés</p>
        </div>
        <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary-700" data-testid="add-employee-button">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter Employé
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Modifier' : 'Nouvel'} Employé</DialogTitle>
              <DialogDescription>
                {editMode ? 'Modifiez les informations' : 'Ajoutez un nouvel employé'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isCrossCompany && !editMode && (
                <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded">
                  <Label htmlFor="company_id" className="font-semibold text-blue-900">
                    Entreprise * <span className="text-xs text-blue-600">(requis pour votre rôle)</span>
                  </Label>
                  <select
                    id="company_id"
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                    required
                    data-testid="employee-company-select"
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
                  >
                    <option value="">-- Sélectionnez une entreprise --</option>
                    {creatableCompanies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="matricule">Matricule *</Label>
                  <Input
                    id="matricule"
                    value={formData.matricule}
                    onChange={(e) => setFormData({ ...formData, matricule: e.target.value })}
                    required
                    data-testid="employee-matricule-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="biometric_id">ID Biométrique</Label>
                  <Input
                    id="biometric_id"
                    value={formData.biometric_id}
                    onChange={(e) => setFormData({ ...formData, biometric_id: e.target.value })}
                    placeholder="Sera configuré après création"
                    data-testid="employee-biometric-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    data-testid="employee-firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    data-testid="employee-lastname-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="employee-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="employee-phone-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Categorie *</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full rounded-md border border-slate-300 p-2"
                    required
                    data-testid="employee-category-select"
                  >
                    <option value="">-- Choisir une categorie --</option>
                    {employeeOptions.categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="position">Poste *</Label>
                  <select
                    id="position"
                    value={formData.position}
                    onChange={(e) => {
                      const newPosition = e.target.value;
                      setFormData(applyAllowanceDefaults(newPosition, formData.base_salary, {
                        ...formData,
                        position: newPosition,
                      }));
                    }}
                    className="w-full rounded-md border border-slate-300 p-2"
                    required
                    data-testid="employee-position-select"
                  >
                    <option value="">-- Choisir un poste --</option>
                    {employeeOptions.positions.map((pos) => (
                      <option key={pos} value={pos}>{pos}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Checkbox Agent en placement */}
              <div className="flex items-start gap-3 p-3 rounded-lg border border-orange-200 bg-orange-50">
                <input
                  type="checkbox"
                  id="is_placement"
                  checked={formData.is_placement || false}
                  onChange={(e) => setFormData({ ...formData, is_placement: e.target.checked })}
                  className="mt-1 w-4 h-4 accent-orange-500"
                  data-testid="is-placement-checkbox"
                />
                <div>
                  <label htmlFor="is_placement" className="font-medium text-orange-800 cursor-pointer select-none">
                    Agent en placement (sous-traitance)
                  </label>
                  <p className="text-xs text-orange-600 mt-0.5">
                    La fiche de paie ne sera pas visible sur le portail de cet employé.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="base_salary">Salaire de base *</Label>
                  <Input
                    id="base_salary"
                    type="number"
                    step="0.01"
                    value={formData.base_salary}
                    onChange={(e) => {
                      const newSalary = e.target.value;
                      setFormData(applyAllowanceDefaults(formData.position, newSalary, {
                        ...formData,
                        base_salary: newSalary,
                      }));
                    }}
                    required
                    data-testid="employee-salary-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="function">Fonction</Label>
                  <Input
                    id="function"
                    value={formData.function}
                    onChange={(e) => setFormData({ ...formData, function: e.target.value })}
                    placeholder="Ex: Responsable comptabilite"
                    data-testid="employee-function-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contract_type">Contrat *</Label>
                  <select
                    id="contract_type"
                    value={formData.contract_type}
                    onChange={(e) => setFormData({ ...formData, contract_type: e.target.value })}
                    className="w-full rounded-md border border-slate-300 p-2"
                    required
                    data-testid="employee-contract-select"
                  >
                    <option value="">-- Type de contrat --</option>
                    {employeeOptions.contract_types && employeeOptions.contract_types.map((ct) => (
                      <option key={ct} value={ct}>{ct}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hire_date">Date d'embauche *</Label>
                  <Input
                    id="hire_date"
                    type="date"
                    value={formData.hire_date}
                    onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                    required
                    data-testid="employee-hire-date-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">Sexe</Label>
                  <select
                    id="gender"
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full rounded-md border border-slate-300 p-2"
                    data-testid="employee-gender-select"
                  >
                    <option value="">-- Sexe --</option>
                    <option value="Masculin">Masculin</option>
                    <option value="Féminin">Féminin</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="marital_status">Etat civil</Label>
                  <select
                    id="marital_status"
                    value={formData.marital_status}
                    onChange={(e) => setFormData({ ...formData, marital_status: e.target.value })}
                    className="w-full rounded-md border border-slate-300 p-2"
                    data-testid="employee-marital-select"
                  >
                    <option value="">-- Etat civil --</option>
                    {employeeOptions.marital_statuses && employeeOptions.marital_statuses.map((ms) => (
                      <option key={ms} value={ms}>{ms}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="children_count">Nombre d'enfants</Label>
                  <Input
                    id="children_count"
                    type="number"
                    min="0"
                    value={formData.children_count}
                    onChange={(e) => setFormData({ ...formData, children_count: e.target.value })}
                    data-testid="employee-children-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_security_number">N Securite sociale</Label>
                <Input
                  id="social_security_number"
                  value={formData.social_security_number}
                  onChange={(e) => setFormData({ ...formData, social_security_number: e.target.value })}
                  placeholder="Numero de securite sociale"
                  data-testid="employee-ssn-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>
                    Prime de transport
                    {formData.position && TRANSPORT_BY_POSITION[formData.position] !== undefined && (
                      <span className="ml-2 text-xs font-normal text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                        Auto ({TRANSPORT_BY_POSITION[formData.position]} USD / {formData.position})
                      </span>
                    )}
                  </Label>
                  <Input
                    type="number" step="0.01"
                    value={formData.transport_allowance}
                    onChange={(e) => setFormData({...formData, transport_allowance: e.target.value})}
                    placeholder="Montant (optionnel)"
                    data-testid="employee-transport-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    Prime de logement
                    {formData.base_salary && parseFloat(formData.base_salary) > 0 && (
                      <span className="ml-2 text-xs font-normal text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                        Auto (30% du salaire)
                      </span>
                    )}
                  </Label>
                  <Input
                    type="number" step="0.01"
                    value={formData.housing_allowance}
                    onChange={(e) => setFormData({...formData, housing_allowance: e.target.value})}
                    placeholder="Montant (optionnel)"
                    data-testid="employee-housing-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="department_id">Direction</Label>
                <select
                  id="department_id"
                  value={formData.department_id}
                  onChange={(e) => setFormData({ ...formData, department_id: e.target.value })}
                  className="w-full rounded-md border border-slate-300 p-2"
                  data-testid="employee-department-select"
                >
                  <option value="">-- Aucune direction --</option>
                  {departments.filter(d => d.is_active !== false).map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-primary" data-testid="submit-employee-button">
                  {editMode ? 'Modifier' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Biometric Enrollment Modal */}
      <Dialog open={biometricModalOpen} onOpenChange={setBiometricModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Fingerprint className="w-5 h-5 text-primary" />
              Employé créé avec succès
            </DialogTitle>
            <DialogDescription>
              Notez les informations de connexion et procédez à l'enregistrement biométrique.
            </DialogDescription>
          </DialogHeader>
          
          {newEmployee && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">Employé créé :</h4>
                <p className="text-sm text-slate-600">
                  <strong>{newEmployee.first_name} {newEmployee.last_name}</strong><br />
                  Matricule : {newEmployee.matricule}<br />
                  Email : {newEmployee.email}
                </p>
              </div>

              {/* Temporary Password Display - CRITICAL */}
              <div className="bg-amber-50 border-2 border-amber-400 p-4 rounded-lg">
                <h4 className="font-bold text-amber-900 flex items-center gap-2 mb-2">
                  <AlertCircle className="w-5 h-5" />
                  IMPORTANT: Mot de passe temporaire
                </h4>
                <div className="bg-white border border-amber-300 p-3 rounded-md">
                  <code className="text-lg font-mono font-bold text-amber-800 select-all" data-testid="temp-password">
                    {newEmployee.temporary_password}
                  </code>
                </div>
                <p className="text-xs text-amber-700 mt-2">
                  Communiquez ce mot de passe de manière sécurisée à l'employé. 
                  Il devra le changer et activer la 2FA à sa première connexion.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 flex items-center gap-2 mb-2">
                  <Fingerprint className="w-4 h-4" />
                  Instructions d'enregistrement biométrique
                </h4>
                <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                  <li>Rendez-vous sur le pointeur biométrique ZKTeco</li>
                  <li>Connectez-vous en mode administrateur</li>
                  <li>Allez dans <strong>Menu → Utilisateur → Nouvel utilisateur</strong></li>
                  <li>Entrez l'ID biométrique ci-dessous</li>
                  <li>Suivez les instructions pour scanner les empreintes</li>
                </ol>
              </div>

              {devices.length > 0 && (
                <div className="text-sm text-slate-600">
                  <p className="font-medium mb-1">Pointeurs disponibles :</p>
                  <ul className="list-disc list-inside">
                    {devices.filter(d => d.is_active).map(device => (
                      <li key={device.id}>{device.device_name} - {device.location}</li>
                    ))}
                  </ul>
                </div>
              )}

              <BiometricIdForm 
                employee={newEmployee}
                devices={devices}
                onSave={handleBiometricEnrollment}
                onSkip={() => {
                  setBiometricModalOpen(false);
                  resetForm();
                  toast.info('Vous pourrez configurer l\'ID biométrique plus tard en modifiant l\'employé.');
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Liste des Employés
            </CardTitle>
            <div className="flex gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 w-64"
                  data-testid="search-input"
                />
              </div>
              <Button onClick={handleSearch} variant="outline" data-testid="search-button">
                Rechercher
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-semibold">Matricule</TableHead>
                  <TableHead className="font-semibold">Nom</TableHead>
                  <TableHead className="font-semibold">Direction</TableHead>
                  <TableHead className="font-semibold">Categorie</TableHead>
                  <TableHead className="font-semibold">Poste</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                  <TableHead className="font-semibold">Biometrie</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-slate-500 py-8">
                      Aucun employe trouve
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredEmployees.map((employee) => (
                    <TableRow key={employee.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono font-medium">{employee.matricule}</TableCell>
                      <TableCell className="font-medium">
                        {employee.first_name} {employee.last_name}
                      </TableCell>
                      <TableCell>
                        {employee.department_name ? (
                          <div className="flex items-center gap-2">
                            <Building className="w-4 h-4 text-slate-400" />
                            {employee.department_name}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">Non affecté</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {employee.category ? (
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className="text-xs">{employee.category}</Badge>
                            {employee.is_placement && (
                              <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-300">Placement</Badge>
                            )}
                          </div>
                        ) : (
                          employee.is_placement ? (
                            <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-300">Placement</Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-slate-400" />
                          {employee.position}
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(employee.status || 'active')}
                      </TableCell>
                      <TableCell>
                        {employee.biometric_id ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle2 className="w-4 h-4" />
                            OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-600 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            Non
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/employees/${employee.id}`)}
                            title="Voir profil"
                            data-testid={`view-employee-${employee.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {canManage && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(employee)}
                                title="Modifier"
                                data-testid={`edit-employee-${employee.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {canTransfer && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-600 hover:bg-blue-50"
                                  onClick={() => openTransferModal(employee)}
                                  title="Transférer vers une autre entreprise"
                                  data-testid={`transfer-employee-${employee.id}`}
                                >
                                  <ArrowRightLeft className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-error hover:bg-red-50"
                                onClick={() => handleDelete(employee.id)}
                                title="Supprimer"
                                data-testid={`delete-employee-${employee.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ── Modal de transfert d'entreprise ── */}
      {transferEmployee && (
        <Dialog open={transferModalOpen} onOpenChange={(open) => { if (!transferLoading) setTransferModalOpen(open); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                Transférer l'employé
              </DialogTitle>
              <DialogDescription>
                Transfert de <strong>{transferEmployee.first_name} {transferEmployee.last_name}</strong> vers une autre entreprise.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Info conservées / réinitialisées */}
              <div className="rounded-lg border bg-slate-50 p-3 text-sm space-y-1">
                <p className="font-medium text-slate-700 mb-2">Ce qui sera conservé :</p>
                <p className="text-green-700">✓ Empreintes biométriques</p>
                <p className="text-green-700">✓ Historique des présences</p>
                <p className="text-green-700">✓ Salaire et avantages</p>
                <p className="text-green-700">✓ Contrat et informations personnelles</p>
                <p className="text-green-700">✓ Accès à l'application</p>
                <p className="text-amber-600 mt-2">⚠ Département : sera réinitialisé</p>
              </div>

              {/* Sélection entreprise cible */}
              <div className="space-y-1">
                <Label htmlFor="transfer-company">Entreprise de destination <span className="text-red-500">*</span></Label>
                {transferCompanies.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">Aucune autre entreprise disponible.</p>
                ) : (
                  <select
                    id="transfer-company"
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={transferTargetCompanyId}
                    onChange={(e) => setTransferTargetCompanyId(e.target.value)}
                  >
                    <option value="">-- Sélectionner une entreprise --</option>
                    {transferCompanies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Raison */}
              <div className="space-y-1">
                <Label htmlFor="transfer-reason">Raison du transfert (optionnel)</Label>
                <textarea
                  id="transfer-reason"
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={3}
                  placeholder="Ex : mutation, restructuration..."
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setTransferModalOpen(false)}
                disabled={transferLoading}
              >
                Annuler
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleTransfer}
                disabled={!transferTargetCompanyId || transferLoading}
                data-testid="confirm-transfer-btn"
              >
                {transferLoading ? 'Transfert en cours...' : 'Confirmer le transfert'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// Sub-component for biometric ID form with device sync
function BiometricIdForm({ employee, devices, onSave, onSkip }) {
  const [biometricId, setBiometricId] = useState(employee.matricule);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [syncing, setSyncing] = useState(false);
  
  const activeDevices = devices.filter(d => d.is_active !== false);
  
  const toggleDevice = (serial) => {
    setSelectedDevices(prev => 
      prev.includes(serial)
        ? prev.filter(s => s !== serial)
        : [...prev, serial]
    );
  };
  
  const selectAllDevices = () => {
    setSelectedDevices(activeDevices.map(d => d.serial_number || d.device_id));
  };

  const handleSave = async () => {
    if (!biometricId) return;
    
    setSyncing(true);
    try {
      // First save the biometric ID
      await onSave(biometricId);
      
      // Then sync to selected devices if any
      if (selectedDevices.length > 0 && employee.id) {
        try {
          await api.post(`/employees/${employee.id}/sync`, {
            device_ids: selectedDevices
          });
          toast.success(`Synchronisé vers ${selectedDevices.length} pointeur(s)`);
        } catch (syncError) {
          toast.warning("ID enregistré mais la synchronisation a échoué. Vous pouvez resynchroniser depuis le profil de l'employé.");
        }
      }
    } finally {
      setSyncing(false);
    }
  };
  
  return (
    <div className="space-y-4 pt-2 border-t">
      <div className="space-y-2">
        <Label htmlFor="new_biometric_id">ID Biométrique (PIN) *</Label>
        <Input
          id="new_biometric_id"
          value={biometricId}
          onChange={(e) => setBiometricId(e.target.value)}
          placeholder="Ex: 001, EMP001..."
        />
        <p className="text-xs text-slate-500">
          Cet ID sera utilisé comme PIN pour identifier l'employé sur les pointeuses.
        </p>
      </div>
      
      {/* Device Selection */}
      {activeDevices.length > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <Label>Synchroniser vers les pointeuses</Label>
            {activeDevices.length > 1 && (
              <Button variant="ghost" size="sm" onClick={selectAllDevices} className="text-xs">
                Tout sélectionner
              </Button>
            )}
          </div>
          <div className="max-h-32 overflow-y-auto space-y-2 border rounded-lg p-2">
            {activeDevices.map((device) => {
              const serial = device.serial_number || device.device_id;
              return (
                <div 
                  key={serial}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    selectedDevices.includes(serial) ? 'bg-indigo-50' : 'hover:bg-slate-50'
                  }`}
                  onClick={() => toggleDevice(serial)}
                >
                  <input
                    type="checkbox"
                    checked={selectedDevices.includes(serial)}
                    onChange={() => toggleDevice(serial)}
                    className="rounded border-slate-300"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{device.device_name || serial}</p>
                    <p className="text-xs text-slate-500">{device.location || 'Emplacement non défini'}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-500">
            L'employé sera créé sur les pointeuses sélectionnées. Il devra ensuite enregistrer son empreinte sur chaque appareil.
          </p>
        </div>
      )}
      
      <div className="flex gap-2">
        <Button variant="outline" onClick={onSkip} className="flex-1" disabled={syncing}>
          Configurer plus tard
        </Button>
        <Button 
          onClick={handleSave} 
          className="flex-1 bg-primary"
          disabled={!biometricId || syncing}
        >
          {syncing ? (
            <>Synchronisation...</>
          ) : (
            <>
              <Fingerprint className="w-4 h-4 mr-2" />
              {selectedDevices.length > 0 
                ? `Enregistrer et synchroniser (${selectedDevices.length})`
                : 'Enregistrer l\'ID'}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
